import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FlaskConical, ArrowDown } from "lucide-react";
import { LabButton } from "./LabButton";
import { IMAGES } from "../data/site";
import { EASE } from "../lib/motion";

const LINES = [
  [{ t: "THE PERFECT", c: "yellow" }],
  [{ t: "CRUNCH IS NOT", c: "yellow" }],
  [
    { t: "AN ", c: "yellow" },
    { t: "ACCIDENT.", c: "red" },
  ],
];

const PARTICLES = [
  { left: "12%", size: 6, color: "#FDBC1F", opacity: 0.5, duration: 15, delay: 0 },
  { left: "24%", size: 4, color: "#EC2027", opacity: 0.45, duration: 19, delay: 3 },
  { left: "46%", size: 5, color: "#FDBC1F", opacity: 0.4, duration: 17, delay: 6 },
  { left: "63%", size: 7, color: "#FDBC1F", opacity: 0.5, duration: 21, delay: 1.5 },
  { left: "78%", size: 4, color: "#EC2027", opacity: 0.5, duration: 16, delay: 4.5 },
  { left: "88%", size: 5, color: "#FDBC1F", opacity: 0.4, duration: 20, delay: 8 },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.28, 0.6]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-screen min-h-[640px] items-center overflow-hidden bg-charcoal grain"
    >
      {/* Background image */}
      <motion.div style={{ y: imgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <img
          src={IMAGES.hero}
          alt="Chicken Labb branded packaging and fried chicken"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover object-[68%_center] animate-hero-zoom"
        />
      </motion.div>

      {/* Warm spotlight on the product (right) */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_65%_at_70%_45%,rgba(253,188,31,0.22)_0%,rgba(253,188,31,0.06)_38%,transparent_68%)]" />

      {/* Subtle overall darken (scroll-reactive) */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-charcoal"
      />

      {/* Left scrim for text readability */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(35,31,32,0.94)_0%,rgba(35,31,32,0.7)_30%,rgba(35,31,32,0.15)_58%,transparent_78%)]" />

      {/* Bottom fade into next section */}
      <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(to_top,rgba(35,31,32,0.9),transparent)]" />

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            className="absolute block rounded-full"
            style={{
              left: p.left,
              bottom: "-10%",
              width: p.size,
              height: p.size,
              background: p.color,
              opacity: p.opacity,
            }}
            animate={{ y: ["0%", "-1200%"], opacity: [0, p.opacity, p.opacity, 0] }}
            transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "linear" }}
          />
        ))}
      </div>

      {/* Faded flask outline bottom-right */}
      <FlaskConical
        className="pointer-events-none absolute bottom-8 right-6 h-40 w-40 text-labyellow opacity-[0.07] sm:h-64 sm:w-64"
        strokeWidth={1}
      />

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-10">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-labyellow"
            data-testid="hero-eyebrow"
          >
            NEW JERSEY // ENGINEERED BY HAND
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-4xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          >
            {LINES.map((line, li) => (
              <span key={li} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.3 + li * 0.14, duration: 0.8, ease: EASE }}
                >
                  {line.map((seg, si) => (
                    <span
                      key={si}
                      className={seg.c === "red" ? "text-labred" : "text-labyellow"}
                    >
                      {seg.t}
                    </span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="mt-6 max-w-md font-body text-base text-offwhite/85 sm:text-lg"
            data-testid="hero-subhead"
          >
            It&apos;s engineered. We turned flavor into a formula so all you have to do is taste it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <LabButton to="/menu" variant="red" testId="hero-see-menu-button">
              See the Menu
            </LabButton>
            <LabButton to="/story" variant="ghostWhite" testId="hero-our-story-button">
              Our Story
            </LabButton>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-7 left-6 z-10 flex items-center gap-3 sm:left-10" data-testid="scroll-cue">
        <div className="flex h-9 w-5 items-start justify-center rounded-full border border-offwhite/40 pt-1.5">
          <span className="h-2 w-0.5 animate-cue rounded-full bg-labyellow" />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-offwhite/50">
          Scroll
        </span>
        <ArrowDown className="h-3 w-3 animate-cue text-labyellow" />
      </div>
    </section>
  );
};
